import { isNativeShell } from "../config/appConfig";

/**
 * v2.224.07 — Gate de segurança para device nativo não verificado.
 * Fonte de verdade: deviceStatus explícito vindo do servidor (check_status).
 * Não depende de presença de código local — device revoked sem código ativo
 * deve continuar bloqueado.
 *
 * @param {string} deviceStatus — valor vindo de check_status: "verified", "active",
 *   "pending", "revoked", "stale", "not_registered", "" ou null.
 * @returns {boolean} true se o gate deve estar ativo (sync e módulos bloqueados).
 */
export function isNativeDeviceDataGateLocked(deviceStatus) {
  if (!isNativeShell()) return false;
  const s = String(deviceStatus || "").toLowerCase().trim();
  return s !== "verified" && s !== "active";
}

export const NATIVE_GATE_MESSAGES = {
  pending: {
    title: "Este dispositivo ainda está pendente de aprovação.",
    body: "Este dispositivo não está autorizado a sincronizar com o servidor no momento.\nOs dados locais, se existirem, podem estar desatualizados e não representar a situação atual do sistema.\n\nPeça ao Administrador para aprovar este aparelho.",
  },
  revoked: {
    title: "Este dispositivo foi revogado pelo Administrador.",
    body: "O acesso deste dispositivo foi revogado pelo Administrador. A sincronização foi bloqueada e os dados locais sensíveis serão limpos deste aparelho.\nOs dados locais não devem ser considerados válidos.\n\nEntre em contato com o Administrador.",
  },
};

/**
 * Retorna a mensagem correta baseada no deviceStatus.
 * @param {string} deviceStatus
 * @returns {{ title: string, body: string }}
 */
export function getNativeGateMessage(deviceStatus) {
  const s = String(deviceStatus || "").toLowerCase().trim();
  if (s === "revoked") return NATIVE_GATE_MESSAGES.revoked;
  if (s === "stale") return { title: "Este dispositivo precisa ser revalidado.", body: "Este dispositivo ultrapassou o prazo configurado sem check-in válido com o servidor.\nA sincronização foi bloqueada e os dados locais podem não representar a situação atual do sistema.\n\nUse Verificar aprovação ou entre em contato com o Administrador." };
  if (s === "not_registered" || s === "unknown") return { title: "Este dispositivo ainda não foi reconhecido pelo servidor.", body: "Este dispositivo não está autorizado a sincronizar com o servidor no momento.\nOs dados locais, se existirem, podem estar desatualizados e não representar a situação atual do sistema.\n\nPeça ao Administrador para registrar e aprovar este aparelho." };
  return NATIVE_GATE_MESSAGES.pending;
}


export const NATIVE_DEVICE_REVOKED_PURGE_START_EVENT = "fz:native-device-revoked-purge-start";
export const NATIVE_DEVICE_REVOKED_PURGE_DONE_EVENT = "fz:native-device-revoked-purge";

let nativeRevokedPurgeInFlight = false;

function dispatchNativeRevokedPurgeEvent(type, marker) {
  try {
    window.dispatchEvent(new CustomEvent(type, { detail: marker }));
  } catch (_) {}
}

function clearInMemoryOperationalCaches(farmId = "") {
  try {
    const live = window.__fzLiveCollections;
    if (live && typeof live === "object") {
      Object.keys(live).forEach((key) => {
        const cleanKey = String(key || "");
        if (!cleanKey || cleanKey.startsWith("auth:") || cleanKey === "fz:farms" || cleanKey.includes("adminGlobalsConfig")) return;
        if (farmId && cleanKey.startsWith(`fz:${farmId}:`)) live[cleanKey] = [];
        else if (!farmId && cleanKey.startsWith("fz:")) live[cleanKey] = [];
        else if (["stock", "people", "suppliers", "providers", "animalBuyers"].includes(cleanKey)) live[cleanKey] = [];
      });
    }
  } catch (_) {}
}

function openIndexedDbForPurge(name, version = undefined) {
  return new Promise((resolve) => {
    try {
      if (typeof indexedDB === "undefined" || !name) {
        resolve(null);
        return;
      }
      const req = version ? indexedDB.open(name, version) : indexedDB.open(name);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch (_) {
      resolve(null);
    }
  });
}

async function clearIndexedDbStores(name, version = undefined) {
  const db = await openIndexedDbForPurge(name, version);
  if (!db) return false;
  const storeNames = Array.from(db.objectStoreNames || []);
  if (!storeNames.length) {
    try { db.close(); } catch (_) {}
    return true;
  }
  return await new Promise((resolve) => {
    try {
      const tx = db.transaction(storeNames, "readwrite");
      for (const storeName of storeNames) {
        try { tx.objectStore(storeName).clear(); } catch (_) {}
      }
      tx.oncomplete = () => { try { db.close(); } catch (_) {}; resolve(true); };
      tx.onerror = () => { try { db.close(); } catch (_) {}; resolve(false); };
      tx.onabort = () => { try { db.close(); } catch (_) {}; resolve(false); };
    } catch (_) {
      try { db.close(); } catch (__unused) {}
      resolve(false);
    }
  });
}

function deleteIndexedDbDatabase(name) {
  return new Promise((resolve) => {
    try {
      if (typeof indexedDB === "undefined" || !name) {
        resolve(false);
        return;
      }
      const req = indexedDB.deleteDatabase(name);
      req.onsuccess = () => resolve(true);
      req.onerror = () => resolve(false);
      req.onblocked = () => resolve(false);
    } catch (_) {
      resolve(false);
    }
  });
}

async function deleteKnownTremboIndexedDbs() {
  const explicitNames = [
    "fazenda-hostgator-offline",
    "fazenda-hostgator-sync",
    "trembo-offline",
    "trembo-sync",
    "fz-offline",
    "fz-sync",
  ];
  const discoveredNames = [];
  try {
    if (typeof indexedDB !== "undefined" && typeof indexedDB.databases === "function") {
      const dbs = await indexedDB.databases();
      for (const db of dbs || []) {
        const name = String(db?.name || "").trim();
        const clean = name.toLowerCase();
        if (!name) continue;
        if (clean.includes("fazenda") || clean.includes("hostgator") || clean.includes("trembo") || clean.startsWith("fz")) {
          discoveredNames.push(name);
        }
      }
    }
  } catch (_) {}

  const names = Array.from(new Set([...explicitNames, ...discoveredNames].filter(Boolean)));
  await Promise.all(names.map((name) => deleteIndexedDbDatabase(name).catch(() => false)));
}

export async function purgeNativeRevokedLocalData({ farmId = "", deviceId = "" } = {}) {
  if (!isNativeShell() || nativeRevokedPurgeInFlight) return { skipped: true };
  nativeRevokedPurgeInFlight = true;
  const preservedDeviceId = (() => {
    try { return deviceId || localStorage.getItem("fz:device_id") || ""; } catch (_) { return deviceId || ""; }
  })();
  const marker = {
    status: "revoked",
    farmId: String(farmId || ""),
    deviceId: preservedDeviceId,
    purgedAt: new Date().toISOString(),
    reason: "native-device-revoked",
  };

  // v2.225.06: avisa a UI antes de limpar storage/mostrar logout para que
  // hooks e Home zerem imediatamente os dados sensíveis já carregados em memória.
  clearInMemoryOperationalCaches(String(farmId || ""));
  dispatchNativeRevokedPurgeEvent(NATIVE_DEVICE_REVOKED_PURGE_START_EVENT, marker);

  // v2.225.04: purge real de dados operacionais no app nativo revogado.
  // Limpar localStorage/sessionStorage não é suficiente porque as coleções grandes
  // vivem no IndexedDB. Primeiro limpamos stores conhecidas; depois pedimos delete
  // dos bancos para cobrir reaberturas futuras. O deviceId e o marcador mínimo de
  // revogação são recolocados no localStorage para o app continuar identificado.
  try { sessionStorage.clear(); } catch (_) {}
  try { localStorage.clear(); } catch (_) {}
  try { localStorage.removeItem("syncQueue"); } catch (_) {}

  await clearIndexedDbStores("fazenda-hostgator-offline", 1).catch(() => false);
  await clearIndexedDbStores("fazenda-hostgator-sync", 3).catch(() => false);
  // v2.226.00: além dos bancos conhecidos, apaga qualquer IndexedDB do app
  // descoberto pelo runtime. Isso fecha cache residual quando o nome/versão
  // do banco muda entre entregas.
  await deleteKnownTremboIndexedDbs().catch(() => false);

  try {
    if (typeof caches !== "undefined") {
      const keys = await caches.keys();
      await Promise.all(keys
        .filter((key) => {
          const clean = String(key || "").toLowerCase();
          return clean.includes("fazenda") || clean.includes("trembo") || clean.includes("hostgator");
        })
        .map((key) => caches.delete(key)));
    }
  } catch (_) {}

  try { if (preservedDeviceId) localStorage.setItem("fz:device_id", preservedDeviceId); } catch (_) {}
  try { localStorage.setItem("fz:nativeDeviceGate:revoked", JSON.stringify(marker)); } catch (_) {}
  clearInMemoryOperationalCaches(String(farmId || ""));
  dispatchNativeRevokedPurgeEvent(NATIVE_DEVICE_REVOKED_PURGE_DONE_EVENT, marker);
  nativeRevokedPurgeInFlight = false;
  return { ok: true, marker };
}
